import { Injectable, inject } from '@angular/core';
import { NotificationStore, PushPermissionStatus } from '../stores/notification.store';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private notificationStore = inject(NotificationStore);

  // ==================== Expose Store Signals ====================
  readonly isLoading = this.notificationStore.loading;
  readonly subscribed = this.notificationStore.subscribed;
  readonly permissionStatus = this.notificationStore.permissionStatus;
  readonly error = this.notificationStore.error;
  readonly isServiceWorkerEnabled = this.notificationStore.isServiceWorkerEnabled;
  readonly vapidPublicKey = this.notificationStore.vapidPublicKey;

  // ==================== Computed Signals ====================
  readonly canSubscribe = this.notificationStore.canSubscribe;
  readonly isGranted = this.notificationStore.isGranted;
  readonly isDenied = this.notificationStore.isDenied;
  readonly isPending = this.notificationStore.isPending;

  // ==================== Backward Compatibility Properties ====================

  get PermissionStatus(): PushPermissionStatus {
    return this.notificationStore.permissionStatus();
  }

  // ==================== Methods ====================

  async subscribeToNotifications(
    resolve?: (status: PushPermissionStatus) => void,
    reject?: (reason: string) => void
  ): Promise<PushPermissionStatus> {
    try {
      const status = await this.notificationStore.subscribeToNotifications();

      if (resolve) {
        // Backward compatibility: call resolve callback
        setTimeout(() => resolve(status), 500);
      }

      return status;
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';

      if (reject) {
        // Backward compatibility: call reject callback
        reject(errorMsg);
      }

      throw error;
    }
  }

  async acknowledgeNotification(notificationID: number): Promise<void> {
    return this.notificationStore.acknowledgeNotification(notificationID);
  }

  async onTest(): Promise<void> {
    return this.notificationStore.sendTestNotification();
  }

  async getVapidPublicKey(): Promise<string> {
    return this.notificationStore.getVapidPublicKey();
  }

  clearError(): void {
    this.notificationStore.clearError();
  }

  clear(): void {
    this.notificationStore.clear();
  }
}
